import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { GsDropdownController } from 'libs/shared/src/controllers/gs-dropdown/gs-dropdown.controller';

/**
 * Styling inputs shared by the rendered dropdown menus (`dropdown`, `megamenu`).
 * The behaviour lives in `GsDropdownController`, the look in this template layer.
 * A component (not a directive) because Angular forbids directives extending components.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: ''
})
export class DropdownStyleBase extends GsDropdownController {
  // Inputs & Models
  /** Classes of the menu container (background, border, shadow, width) */
  readonly menuClasses = input('bg-light-shine divide-y divide-medium-main rounded-lg shadow w-44 gap-4');
  /** Layout, spacing and typography of dropdown items — never colors, those belong in `menuItemColorClasses` */
  readonly menuItemClasses = input('px-4 py-2 text-sm');
  /** Base color classes (text + bg) of a menu item — replaced by `activeMenuItemClasses` on the current page */
  readonly menuItemColorClasses = input('text-dark-main hover:text-dark-main');
  /** CSS classes applied to active (current page) menu items */
  readonly activeMenuItemClasses = input('');
  /** Tailwind hover classes (text + bg, with `hover:` prefix) applied to interactive menu items */
  readonly hoverMenuItemClasses = input('');
  /** Whether active menu items should be underlined */
  readonly activeUnderlined = input(false);
  /** Whether to render photos on sub-menu items (only rendered inside submenus, never at the root level) */
  readonly showItemPhotos = input(true);
  /** Whether to render glyph icons on root-level items (only rendered at the root level, never inside submenus) */
  readonly showItemGlyphs = input(true);

  // Methods
  /**
   * Returns the color classes for a menu item: the active colors on the current page,
   * the base colors otherwise. Both are Tailwind text/bg utilities of equal specificity,
   * so applying them together would let the generated CSS order decide the winner —
   * they must never end up on the same element.
   * @param active Whether the item points to the current page
   * @returns The color classes to apply to the item
   */
  itemColorClasses(active: boolean): string {
    return (active && this.activeMenuItemClasses()) || this.menuItemColorClasses();
  }
}
